import { joinDictatedDateAcrossChunks } from "./dictationDates";
import { normalizeSpokenDictation } from "./spokenDictationRules";

export interface NoteDictationInterimRange {
  start: number;
  end: number;
  text: string;
}

export interface NoteDictationInsertResult {
  value: string;
  cursorPosition: number;
  interimRange: NoteDictationInterimRange | null;
}

export interface NoteDictationEditSyncResult {
  interimRange: NoteDictationInterimRange | null;
  wasInterimRangeInvalidated: boolean;
  selection: {
    start: number;
    end: number;
  };
}

interface RebaseNoteDictationInterimRangeOptions {
  previousValue?: string;
  nextValue?: string;
  interimRange?: NoteDictationInterimRange | null;
}

interface InsertNoteDictationTextOptions {
  currentValue?: string;
  transcript?: string;
  selectionStart?: number | null;
  selectionEnd?: number | null;
  previousInterimRange?: NoteDictationInterimRange | null;
  isFinal?: boolean;
  /** First words of a dictation session, which Deepgram always capitalizes. */
  isSessionStart?: boolean;
}

interface SyncNoteDictationAfterEditOptions extends RebaseNoteDictationInterimRangeOptions {
  selectionStart?: number | null;
  selectionEnd?: number | null;
}

const SENTENCE_START_PATTERN = /(^|[.!?\n])[ \t]*$/;
const LEADING_CLOSING_PUNCTUATION_PATTERN = /^[,.;:!?)]/;
// Only common words are lowercased, so drug and proper names like "Tylenol" keep their capital.
const COMMON_SENTENCE_START_WORDS = new Set([
  "a",
  "an",
  "the",
  "this",
  "that",
  "these",
  "those",
  "and",
  "but",
  "or",
  "so",
  "then",
  "also",
  "as",
  "if",
  "because",
  "which",
  "who",
  "she",
  "he",
  "they",
  "it",
  "we",
  "her",
  "his",
  "their",
  "its",
  "our",
  "my",
  "your",
  "patient",
  "no",
  "not",
  "with",
  "without",
  "in",
  "on",
  "at",
  "for",
  "from",
  "to",
  "of",
  "by",
  "after",
  "before",
  "since",
  "about",
  "over",
  "under",
  "is",
  "was",
  "are",
  "were",
  "has",
  "had",
  "have",
  "will",
  "can",
  "may",
  "daily",
  "twice",
  "once",
  "denies",
  "reports",
  "states",
  "presents",
  "complains",
  "mild",
  "moderate",
  "severe",
  "chronic",
  "acute",
  "left",
  "right",
  "bilateral",
  "some",
  "all",
  "any",
  "more",
  "less",
]);
const LEADING_WORD_PATTERN = /^([A-Z][a-z]*)\b/;
const TITLE_PATTERN = /^(?:Dr|Mr|Mrs|Ms|Prof)\b/;

// Deepgram capitalizes the first word of a session even when it continues a sentence.
const matchSentenceCasing = (transcript: string, textBefore: string): string => {
  if (SENTENCE_START_PATTERN.test(textBefore) || TITLE_PATTERN.test(transcript)) return transcript;
  const leadingWord = transcript.match(LEADING_WORD_PATTERN)?.[1];
  if (!leadingWord || !COMMON_SENTENCE_START_WORDS.has(leadingWord.toLowerCase())) {
    return transcript;
  }
  return `${leadingWord.toLowerCase()}${transcript.slice(leadingWord.length)}`;
};

const SENTENCE_END_PATTERN = /[.!?][ \t]*$/;
const ABBREVIATION_END_PATTERN = /\b(?:dr|mr|mrs|ms|vs|approx|etc|e\.g|i\.e)\.[ \t]*$/i;

// A spoken "period" at the end of the previous piece is ours, not Deepgram's, so Deepgram
// doesn't know to capitalize the next piece.
const capitalizeAfterSentenceEnd = (transcript: string, textBefore: string): string =>
  SENTENCE_END_PATTERN.test(textBefore) && !ABBREVIATION_END_PATTERN.test(textBefore)
    ? transcript.replace(/^[a-z]/, (letter) => letter.toUpperCase())
    : transcript;

const clampSelectionPosition = (
  position: number | null | undefined,
  valueLength: number,
  fallback: number
): number => {
  if (typeof position !== "number" || !Number.isFinite(position)) return fallback;
  return Math.min(valueLength, Math.max(0, position));
};

export const rebaseNoteDictationInterimRange = ({
  previousValue = "",
  nextValue = "",
  interimRange = null,
}: RebaseNoteDictationInterimRangeOptions): NoteDictationInterimRange | null => {
  if (!interimRange) return null;

  const previousLength = previousValue.length;
  const nextLength = nextValue.length;
  const rangeStart = clampSelectionPosition(interimRange.start, previousLength, 0);
  const rangeEnd = clampSelectionPosition(interimRange.end, previousLength, rangeStart);
  let commonPrefixLength = 0;
  const maximumPrefixLength = Math.min(previousLength, nextLength);

  while (
    commonPrefixLength < maximumPrefixLength &&
    previousValue[commonPrefixLength] === nextValue[commonPrefixLength]
  ) {
    commonPrefixLength += 1;
  }

  let commonSuffixLength = 0;
  const maximumSuffixLength = Math.min(
    previousLength - commonPrefixLength,
    nextLength - commonPrefixLength
  );

  while (
    commonSuffixLength < maximumSuffixLength &&
    previousValue[previousLength - commonSuffixLength - 1] ===
      nextValue[nextLength - commonSuffixLength - 1]
  ) {
    commonSuffixLength += 1;
  }

  const previousEditEnd = previousLength - commonSuffixLength;
  const lengthDelta = nextLength - previousLength;

  if (previousEditEnd <= rangeStart) {
    return {
      ...interimRange,
      start: rangeStart + lengthDelta,
      end: rangeEnd + lengthDelta,
    };
  }

  if (commonPrefixLength >= rangeEnd) {
    return { ...interimRange, start: rangeStart, end: rangeEnd };
  }

  const nextEditEnd = nextLength - commonSuffixLength;
  const editIsContainedInInterimRange =
    commonPrefixLength >= rangeStart && previousEditEnd <= rangeEnd;

  if (editIsContainedInInterimRange) {
    const nextRangeStart = rangeStart;
    const nextRangeEnd = clampSelectionPosition(rangeEnd + lengthDelta, nextLength, nextEditEnd);

    return {
      start: nextRangeStart,
      end: nextRangeEnd,
      text: nextValue.slice(nextRangeStart, nextRangeEnd),
    };
  }

  return null;
};

export const syncNoteDictationAfterEdit = ({
  previousValue = "",
  nextValue = "",
  interimRange = null,
  selectionStart,
  selectionEnd,
}: SyncNoteDictationAfterEditOptions): NoteDictationEditSyncResult => {
  const nextSelectionStart = clampSelectionPosition(selectionStart, nextValue.length, 0);
  const nextSelectionEnd = clampSelectionPosition(
    selectionEnd,
    nextValue.length,
    nextSelectionStart
  );

  const rebasedInterimRange = rebaseNoteDictationInterimRange({
    previousValue,
    nextValue,
    interimRange,
  });

  return {
    interimRange: rebasedInterimRange,
    wasInterimRangeInvalidated: Boolean(interimRange && !rebasedInterimRange),
    selection: {
      start: Math.min(nextSelectionStart, nextSelectionEnd),
      end: Math.max(nextSelectionStart, nextSelectionEnd),
    },
  };
};

export const insertNoteDictationText = ({
  currentValue = "",
  transcript = "",
  selectionStart,
  selectionEnd,
  previousInterimRange = null,
  isFinal = false,
  isSessionStart = false,
}: InsertNoteDictationTextOptions): NoteDictationInsertResult | null => {
  let workingValue = currentValue;
  const normalizedTranscript = transcript.trim();

  if (!normalizedTranscript) {
    return null;
  }

  let nextSelectionStart = clampSelectionPosition(
    selectionStart,
    workingValue.length,
    workingValue.length
  );
  let nextSelectionEnd = clampSelectionPosition(
    selectionEnd,
    workingValue.length,
    nextSelectionStart
  );

  if (nextSelectionEnd < nextSelectionStart) {
    [nextSelectionStart, nextSelectionEnd] = [nextSelectionEnd, nextSelectionStart];
  }

  if (previousInterimRange) {
    const interimStart = clampSelectionPosition(previousInterimRange.start, workingValue.length, 0);
    const interimEnd = clampSelectionPosition(
      previousInterimRange.end,
      workingValue.length,
      interimStart
    );
    const interimRangeStillMatches =
      workingValue.slice(interimStart, interimEnd) === previousInterimRange.text;

    if (interimRangeStillMatches) {
      workingValue = `${workingValue.slice(0, interimStart)}${workingValue.slice(interimEnd)}`;
      nextSelectionStart = interimStart;
      nextSelectionEnd = interimStart;
    }
  }

  let beforeSelection = workingValue.slice(0, nextSelectionStart);
  const afterSelection = workingValue.slice(nextSelectionEnd);
  let datedTranscript = normalizedTranscript;
  // Draft (interim) text is replaced later, so only final text may absorb already-inserted text.
  if (isFinal) {
    const joined = joinDictatedDateAcrossChunks(
      beforeSelection,
      normalizeSpokenDictation(normalizedTranscript, {
        mode: "formatted",
        textBefore: beforeSelection,
      })
    );
    beforeSelection = joined.textBefore;
    datedTranscript = joined.transcript;
  }
  const casedTranscript = capitalizeAfterSentenceEnd(
    isSessionStart ? matchSentenceCasing(datedTranscript, beforeSelection) : datedTranscript,
    beforeSelection
  );
  const prefix =
    beforeSelection.length > 0 &&
    !/\s$/.test(beforeSelection) &&
    !LEADING_CLOSING_PUNCTUATION_PATTERN.test(casedTranscript)
      ? " "
      : "";
  const suffix = afterSelection.length > 0 && !/^\s|^[,.;:!?)]/.test(afterSelection) ? " " : "";
  const insertedText = `${prefix}${casedTranscript}${suffix}`;
  const insertedStart = beforeSelection.length;
  const insertedEnd = insertedStart + insertedText.length;

  return {
    value: `${beforeSelection}${insertedText}${afterSelection}`,
    cursorPosition: insertedEnd,
    interimRange: isFinal ? null : { start: insertedStart, end: insertedEnd, text: insertedText },
  };
};
